import React, { useState, useEffect, useContext } from 'react';
import { AiOutlineClose, AiOutlineShoppingCart, AiOutlineArrowLeft } from 'react-icons/ai';
import { FaCheckCircle, FaCheck } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { TRANSPARENT_PLACEHOLDER, RAZORPAY_KEY, BASE_URL } from '../../utils/constants';
import { FiTrash2 } from 'react-icons/fi';
import { AuthContext } from '../../context/AuthContext';
import { syncLocalStorageCart, fetchCartItems } from '../../utils/cartSync';
import { fetchAddresses } from '../../store/slices/addressSlice';
import AddressModal from './AddressModal';
import AccountModal from './AccountModal';
import https from '../../utils/https';
import { RootState, AppDispatch } from '../../store';
import { toast } from '../../utils/toast';

interface CartModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialStep?: 'cart' | 'checkout' | 'success';
    onOpenOrders?: () => void;
}

const CartSkeleton = () => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {Array(3).fill(0).map((_, i) => (
                <div key={i} className="cart-skeleton-card">
                    <div className="cart-skeleton-img shimmer-effect" />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="cart-skeleton-line shimmer-effect" style={{ width: '80%' }} />
                        <div className="cart-skeleton-line shimmer-effect" style={{ width: '40%', height: '14px' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                            <div className="cart-skeleton-line shimmer-effect" style={{ width: '60px', height: '24px', borderRadius: '12px' }} />
                            <div className="cart-skeleton-line shimmer-effect" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const CartModal = ({ isOpen, onClose, initialStep = 'cart', onOpenOrders }: CartModalProps) => {
    const [cartItems, setCartItems] = useState<any[]>([]);
    const [step, setStep] = useState<'cart' | 'checkout' | 'success'>('cart');
    const [paymentMethod, setPaymentMethod] = useState<'COD' | 'ONLINE'>('COD');
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [placedOrder, setPlacedOrder] = useState<any>(null);

    const { isAuth } = useContext(AuthContext);
    const dispatch = useDispatch<AppDispatch>();
    const { addresses } = useSelector((state: RootState) => state.address);
    const activeAddress = addresses.find(a => a.isDefault) || addresses[0];

    const reduxUser = useSelector((state: RootState) => state.auth.user);
    const name = reduxUser ? {
        firstname: reduxUser.first_name || reduxUser.firstname || '',
        lastname: reduxUser.last_name || reduxUser.lastname || '',
        email: reduxUser.email || reduxUser.username || '',
        phone: reduxUser.phone_number || '',
    } : {};

    const loadCartItems = async (useLocalStorageOnly = false) => {
        setLoading(true);
        try {
            if (isAuth) {
                let items = [];
                if (useLocalStorageOnly) {
                    const cartStr = localStorage.getItem('honeywill_cart');
                    items = cartStr ? JSON.parse(cartStr) : [];
                } else {
                    items = await fetchCartItems();
                }
                setCartItems(items);
            } else {
                const cartStr = localStorage.getItem('honeywill_cart');
                setCartItems(cartStr ? JSON.parse(cartStr) : []);
            }
        } catch (e) {
            console.error("Error loading cart items:", e);
        } finally {
            setLoading(false);
        }
    };

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if ((window as any).Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    useEffect(() => {
        if (isOpen) {
            if (isAuth) {
                syncLocalStorageCart().then(() => loadCartItems(false));
            } else {
                loadCartItems(false);
            }
        }
    }, [isOpen, isAuth]);

    useEffect(() => {
        if (!isOpen) return;

        const handleUpdate = () => {
            loadCartItems(true);
        };

        window.addEventListener('cartUpdated', handleUpdate);
        window.addEventListener('storage', handleUpdate);
        return () => {
            window.removeEventListener('cartUpdated', handleUpdate);
            window.removeEventListener('storage', handleUpdate);
        };
    }, [isOpen, isAuth]);

    useEffect(() => {
        if (isAuth && isOpen) {
            dispatch(fetchAddresses(undefined));
        }
    }, [isAuth, dispatch, isOpen, isAddressModalOpen]);

    const updateQuantity = async (id: string, newQty: number) => {
        if (newQty < 1) return;

        const targetItem = cartItems.find(item => item.id === id);
        const productId = targetItem?.productId || id.split('_')[0];
        let purchaseMode = targetItem?.purchaseMode || 'coil';
        if (purchaseMode === 'single') {
            purchaseMode = 'coil';
        }

        if (isAuth) {
            try {
                await https.post('cart/update-quantity', {
                    product_id: productId,
                    quantity: newQty,
                    purchase_mode: purchaseMode
                });
                loadCartItems();
                window.dispatchEvent(new Event('cartUpdated'));
            } catch (err) {
                console.error("Error updating API cart quantity:", err);
            }
        } else {
            const updated = cartItems.map(item => {
                if (item.id === id) {
                    return {
                        ...item,
                        no: newQty,
                        subtotal: item.priceA * newQty
                    };
                }
                return item;
            });
            localStorage.setItem('honeywill_cart', JSON.stringify(updated));
            setCartItems(updated);
            window.dispatchEvent(new Event('cartUpdated'));
        }
    };

    const handleRemove = async (id: string) => {
        const targetItem = cartItems.find(item => item.id === id);
        const productId = targetItem?.productId || id.split('_')[0];
        let purchaseMode = targetItem?.purchaseMode || 'coil';
        if (purchaseMode === 'single') {
            purchaseMode = 'coil';
        }

        if (isAuth) {
            try {
                await https.post('cart/remove', {
                    product_id: productId,
                    purchase_mode: purchaseMode
                });
                loadCartItems();
                window.dispatchEvent(new Event('cartUpdated'));
            } catch (err) {
                console.error("Error deleting item from API cart:", err);
            }
        } else {
            const updated = cartItems.filter(item => item.id !== id);
            localStorage.setItem('honeywill_cart', JSON.stringify(updated));
            setCartItems(updated);
            window.dispatchEvent(new Event('cartUpdated'));
        }
    };

    const handlePlaceOrder = async () => {
        if (!isAuth) {
            setIsLoginModalOpen(true);
        } else if (!activeAddress) {
            setIsAddressModalOpen(true);
            toast.error("Please add a shipping address to place your order.");
        } else {
            try {
                const addressId = activeAddress._id;

                if (paymentMethod === 'ONLINE') {
                    const loaded = await loadRazorpayScript();
                    if (!loaded) {
                        toast.error("Failed to load Razorpay payment gateway. Please try again.");
                        return;
                    }

                    const options = {
                        key: RAZORPAY_KEY,
                        amount: finalAmount * 100, // in paise
                        currency: "INR",
                        name: "HoneyCool",
                        description: "Premium Purchase Order",
                        image: "https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=120&auto=format&fit=crop&q=80",
                        handler: async function (response: any) {
                            const payment_obj = {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id || `dummy_order_${Date.now()}`,
                                razorpay_signature: response.razorpay_signature || `dummy_sig_${Date.now()}`
                            };

                            try {
                                const res = await https.post('/orders', {
                                    address_id: addressId,
                                    payment_method: 'ONLINE',
                                    payment_obj: payment_obj
                                });

                                if (res.success || res.code === 200 || res.code === 201) {
                                    toast.success("Payment successful & order placed! Thank you for shopping.");
                                    localStorage.removeItem('honeywill_cart');
                                    window.dispatchEvent(new Event('cartUpdated'));
                                    setCartItems([]);
                                    setPlacedOrder(res.data);
                                    setStep('success');
                                } else {
                                    toast.error(res.message || "Failed to place order. Please try again.");
                                }
                            } catch (err: any) {
                                console.error("Error creating online order:", err);
                                toast.error(err.response?.data?.message || err.message || "Failed to place order. Please try again.");
                            }
                        },
                        prefill: {
                            name: `${reduxUser?.first_name || reduxUser?.firstname || activeAddress?.name || 'User'} ${reduxUser?.last_name || reduxUser?.lastname || ''}`.trim(),
                            email: reduxUser?.email || reduxUser?.username || "anshu@gmail.com",
                            contact: reduxUser?.phone_number || activeAddress?.shipping_phone || "1234567890"
                        },
                        theme: {
                            color: "#43c8ca"
                        }
                    };

                    const rzp = new (window as any).Razorpay(options);
                    rzp.open();
                } else {
                    const res = await https.post('/orders', {
                        address_id: addressId,
                        payment_method: 'COD'
                    });

                    if (res.success || res.code === 200 || res.code === 201) {
                        toast.success("Order placed successfully! Thank you for shopping.");
                        localStorage.removeItem('honeywill_cart');
                        window.dispatchEvent(new Event('cartUpdated'));
                        setCartItems([]);
                        setPlacedOrder(res.data);
                        setStep('success');
                    } else {
                        toast.error(res.message || "Failed to place order. Please try again.");
                    }
                }
            } catch (error: any) {
                console.error("Error placing order:", error);
                toast.error(error.response?.data?.message || error.message || "Failed to place order. Please try again.");
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setStep(initialStep);
        } else {
            document.body.style.overflow = 'unset';
            setStep('cart');
            setPlacedOrder(null);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, initialStep]);

    if (!isOpen) return null;

    const totalAmount = cartItems.reduce((acc: number, item: any) => acc + ((item.priceA || 0) * (item.no || 1)), 0);
    const totalDiscount = 0;
    const finalAmount = totalAmount;

    return (
        <div className="cart-modal-overlay" onClick={onClose}>
            <style>
                {`
                .cart-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    justify-content: flex-end;
                    background-color: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    animation: fadeInOverlay 0.3s ease;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }

                .cart-drawer {
                    animation: slideInRight 0.45s cubic-bezier(0.16, 1, 0.3, 1);
                    width: 32% !important;
                    min-width: 440px !important;
                    background-color: #ffffff;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    box-shadow: -15px 0 45px rgba(15, 23, 42, 0.08);
                    border-top-left-radius: 24px;
                    border-bottom-left-radius: 24px;
                    overflow: hidden;
                }

                .cart-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px;
                    border-bottom: 1px solid #f3f4f6;
                    background-color: #ffffff;
                }

                .cart-title-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .cart-title {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 800;
                    color: #1f2937;
                    letter-spacing: -0.5px;
                }

                .cart-count-badge {
                    background: #e6f9fa;
                    color: #14958f;
                    font-size: 11.5px;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 20px;
                    letter-spacing: 0.3px;
                }

                .cart-close-btn {
                    background: #f3f4f6;
                    border: none;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4b5563;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .cart-close-btn:hover {
                    background-color: #e5e7eb;
                    color: #111827;
                    transform: scale(1.05);
                }

                .cart-back-btn {
                    background: none;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #1f2937;
                    padding: 6px;
                    border-radius: 50%;
                    transition: background 0.2s;
                }

                .cart-back-btn:hover {
                    background-color: #f3f4f6;
                }

                .cart-content {
                    flex: 1;
                    overflow-y: auto;
                    background-color: #fcfcfc;
                    padding: 20px 24px;
                }

                .cart-item-card {
                    display: flex;
                    gap: 16px;
                    padding: 16px;
                    background-color: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f3f4f6;
                    align-items: center;
                    margin-bottom: 16px;
                    transition: all 0.25s ease;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.01), 0 2px 4px -1px rgba(0,0,0,0.01);
                }

                .cart-item-card:hover {
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
                    border-color: #e5e7eb;
                }

                .cart-item-img {
                    width: 80px;
                    height: 80px;
                    object-fit: cover;
                    border-radius: 12px;
                    border: 1px solid #f3f4f6;
                    background-color: #f9fafb;
                }

                .cart-item-details {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .cart-item-title {
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #1f2937;
                    display: -webkit-box;
                    WebkitLineClamp: 2;
                    WebkitBoxOrient: 'vertical';
                    overflow: hidden;
                    text-align: left;
                    line-height: 1.4;
                }

                .cart-item-price {
                    font-size: 15px;
                    font-weight: 800;
                    color: #43c8ca;
                    text-align: left;
                }

                .qty-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background-color: #f3f4f6;
                    padding: 3px 6px;
                    border-radius: 20px;
                    width: fit-content;
                }

                .qty-btn {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    background: #ffffff;
                    color: #1f2937;
                    font-weight: bold;
                    font-size: 13px;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
                    transition: all 0.2s;
                }

                .qty-btn:hover:not(:disabled) {
                    background: #e5e7eb;
                }

                .qty-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .qty-count {
                    font-size: 13px;
                    font-weight: 700;
                    color: #1f2937;
                    min-width: 14px;
                    text-align: center;
                }

                .cart-remove-btn {
                    background: none;
                    border: none;
                    color: #ff4757;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;
                    border-radius: 50%;
                    transition: all 0.2s;
                }

                .cart-remove-btn:hover {
                    background-color: #fff5f6;
                    transform: scale(1.05);
                }

                .cart-empty {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #6b7280;
                    padding: 40px 24px;
                    text-align: center;
                }

                .cart-empty-icon-wrapper {
                    width: 90px;
                    height: 90px;
                    background-color: #e6f9fa;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 24px;
                    box-shadow: 0 8px 20px rgba(67, 200, 202, 0.06);
                    color: #43c8ca;
                }

                .cart-empty h3 {
                    font-size: 19px;
                    font-weight: 800;
                    color: #1f2937;
                    margin: 0 0 8px 0;
                    letter-spacing: -0.3px;
                }

                .cart-empty p {
                    font-size: 13.5px;
                    color: #6b7280;
                    margin: 0 0 24px 0;
                    max-width: 280px;
                    line-height: 1.5;
                }

                .cart-empty-cta {
                    padding: 12px 28px;
                    background-color: #43c8ca;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    text-decoration: none;
                    box-shadow: 0 4px 12px rgba(67, 200, 202, 0.2);
                }

                .cart-empty-cta:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(67, 200, 202, 0.3);
                }

                .checkout-section {
                    padding: 20px;
                    background-color: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f3f4f6;
                    margin-bottom: 20px;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.01);
                }

                .checkout-section-title {
                    font-size: 11.5px;
                    font-weight: 800;
                    color: #9ca3af;
                    margin-bottom: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    text-align: left;
                }

                .address-detail-box {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    padding: 16px;
                    border-radius: 12px;
                    transition: all 0.2s;
                }

                .address-detail-box.empty {
                    border: 1px dashed #43c8ca;
                    background: #f0fbfb;
                }

                .address-change-btn {
                    border: 1px solid #43c8ca;
                    background: #ffffff;
                    color: #43c8ca;
                    padding: 6px 14px;
                    font-weight: 700;
                    font-size: 11px;
                    border-radius: 8px;
                    cursor: pointer;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    transition: all 0.2s;
                }

                .address-change-btn:hover {
                    background-color: #43c8ca;
                    color: #ffffff;
                }

                .payment-option-label {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 16px;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: left;
                    border: 1px solid #e5e7eb;
                    background-color: #ffffff;
                }

                .payment-option-label.selected {
                    border-color: #43c8ca;
                    background-color: #f0fbfb;
                }

                .price-summary-row {
                    display: flex;
                    justify-content: space-between;
                    font-size: 13.5px;
                    color: #4b5563;
                }

                .price-summary-total {
                    display: flex;
                    justify-content: space-between;
                    font-size: 16px;
                    font-weight: 800;
                    color: #1f2937;
                    border-top: 1px solid #f3f4f6;
                    padding-top: 12px;
                    margin-top: 8px;
                }

                .success-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 32px 24px;
                    text-align: center;
                }

                .success-icon-container {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background-color: #e6f9fa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    marginBottom: 20px;
                    color: #43c8ca;
                    box-shadow: 0 8px 20px rgba(67, 200, 202, 0.15);
                }

                .success-details-card {
                    width: 100%;
                    border: 1px solid #f3f4f6;
                    border-radius: 16px;
                    text-align: left;
                    margin-bottom: 32px;
                    background-color: #f9fafb;
                    padding: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .success-action-btn {
                    width: 100%;
                    padding: 14px;
                    background-color: #43c8ca;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    box-shadow: 0 4px 14px rgba(67, 200, 202, 0.2);
                }

                .success-action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(67, 200, 202, 0.3);
                }

                .success-secondary-btn {
                    width: 100%;
                    padding: 13px;
                    background-color: white;
                    color: #4b5563;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .success-secondary-btn:hover {
                    background-color: #f9fafb;
                    color: #1f2937;
                }

                /* Skeletons */
                .cart-skeleton-card {
                    display: flex;
                    gap: 16px;
                    padding: 16px;
                    background-color: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f3f4f6;
                    align-items: center;
                    margin-bottom: 16px;
                }

                .cart-skeleton-img {
                    width: 80px;
                    height: 80px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    flex-shrink: 0;
                }

                .cart-skeleton-line {
                    height: 12px;
                    background: #f3f4f6;
                    border-radius: 6px;
                }

                .shimmer-effect {
                    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
                    background-size: 200% 100%;
                    animation: shimmer-anim 1.5s infinite;
                }

                @keyframes shimmer-anim {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }

                @media (max-width: 768px) {
                    .cart-drawer {
                        width: 100% !important;
                        min-width: unset !important;
                        border-radius: 0;
                    }
                    .cart-header {
                        padding: 16px 20px !important;
                    }
                    .cart-content {
                        padding: 16px !important;
                    }
                }
                `}
            </style>

            <div className="cart-drawer" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="cart-header">
                    {step === 'cart' ? (
                        <div className="cart-title-container">
                            <AiOutlineShoppingCart size={22} color="#1f2937" />
                            <h2 className="cart-title">Your Bag</h2>
                            <span className="cart-count-badge">
                                {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                            </span>
                        </div>
                    ) : step === 'checkout' ? (
                        <div className="cart-title-container">
                            <button onClick={() => setStep('cart')} className="cart-back-btn" aria-label="Back to Cart">
                                <AiOutlineArrowLeft size={18} />
                            </button>
                            <h2 className="cart-title">Checkout</h2>
                        </div>
                    ) : (
                        <div className="cart-title-container">
                            <FaCheckCircle size={22} color="#43c8ca" />
                            <h2 className="cart-title">Order Confirmed</h2>
                        </div>
                    )}
                    <button onClick={onClose} className="cart-close-btn" aria-label="Close cart">
                        <AiOutlineClose size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="cart-content">
                    {loading ? (
                        <CartSkeleton />
                    ) : step === 'cart' ? (
                        cartItems.length > 0 ? (
                            <div>
                                {cartItems.map((item: any) => (
                                    <div key={item.id} className="cart-item-card">
                                        <img
                                            src={item.image || TRANSPARENT_PLACEHOLDER}
                                            alt={item.name}
                                            onError={(e) => { (e.target as HTMLImageElement).src = TRANSPARENT_PLACEHOLDER; }}
                                            className="cart-item-img"
                                        />
                                        <div className="cart-item-details">
                                            <div className="cart-item-title">
                                                {item.name}
                                            </div>
                                            <div className="cart-item-price">
                                                ₹{item.priceA?.toLocaleString('en-IN')}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                                <div className="qty-container">
                                                    <button 
                                                        onClick={() => updateQuantity(item.id, item.no - 1)} 
                                                        disabled={item.no <= 1}
                                                        className="qty-btn"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="qty-count">{item.no}</span>
                                                    <button 
                                                        onClick={() => updateQuantity(item.id, item.no + 1)} 
                                                        className="qty-btn"
                                                        aria-label="Increase quantity"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <button onClick={() => handleRemove(item.id)} className="cart-remove-btn" title="Remove Item">
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="cart-empty">
                                <div className="cart-empty-icon-wrapper">
                                    <AiOutlineShoppingCart className="cart-empty-icon" />
                                </div>
                                <h3>Your Bag is Empty</h3>
                                <p>Looks like you haven't added anything to your bag yet.</p>
                                <button onClick={onClose} className="cart-empty-cta">
                                    Continue Shopping
                                </button>
                            </div>
                        )
                    ) : step === 'checkout' ? (
                        <div>
                            {/* Shipping Address */}
                            <div className="checkout-section">
                                <div className="checkout-section-title">Shipping Address</div>
                                {isAuth ? (
                                    activeAddress ? (
                                        <div className="address-detail-box">
                                            <div style={{ fontSize: '13px', color: '#1f2937', textAlign: 'left', flex: 1, paddingRight: '8px' }}>
                                                <span style={{ fontWeight: 700 }}>{activeAddress.name} - {activeAddress.pincode}</span>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', lineHeight: '1.4' }}>
                                                    {activeAddress.address}, {activeAddress.locality}, {activeAddress.city}, {activeAddress.state} | Phone: {activeAddress.shipping_phone}
                                                </div>
                                            </div>
                                            <button onClick={() => setIsAddressModalOpen(true)} className="address-change-btn">
                                                Change
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="address-detail-box empty">
                                            <div style={{ fontSize: '13px', color: '#6b7280', textAlign: 'left' }}>
                                                <span style={{ fontWeight: 700, color: '#43c8ca' }}>No Address Found!</span>
                                                <div style={{ fontSize: '11.5px', marginTop: '4px' }}>Please add a shipping address to proceed.</div>
                                            </div>
                                            <button onClick={() => setIsAddressModalOpen(true)} className="address-change-btn">
                                                + Add
                                            </button>
                                        </div>
                                    )
                                ) : (
                                    <div className="address-detail-box" style={{ borderColor: '#f87171', backgroundColor: '#fef2f2' }}>
                                        <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: 600 }}>Login to add shipping address</div>
                                        <button onClick={() => setIsLoginModalOpen(true)} className="address-change-btn" style={{ borderColor: '#ef4444', color: '#ef4444' }}>
                                            Login
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Payment Method */}
                            {isAuth && (
                                <div className="checkout-section">
                                    <div className="checkout-section-title">Payment Method</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        <label className={`payment-option-label ${paymentMethod === 'COD' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="paymentMethodModal"
                                                value="COD"
                                                checked={paymentMethod === 'COD'}
                                                onChange={() => setPaymentMethod('COD')}
                                                style={{ accentColor: '#43c8ca' }}
                                            />
                                            <div>
                                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Cash on Delivery (COD)</div>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>Pay cash when order is delivered</div>
                                            </div>
                                        </label>

                                        <label className={`payment-option-label ${paymentMethod === 'ONLINE' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="paymentMethodModal"
                                                value="ONLINE"
                                                checked={paymentMethod === 'ONLINE'}
                                                onChange={() => setPaymentMethod('ONLINE')}
                                                style={{ accentColor: '#43c8ca' }}
                                            />
                                            <div>
                                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Pay Online</div>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>Credit/Debit Card, UPI, NetBanking</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Price Summary */}
                            <div className="checkout-section" style={{ backgroundColor: '#f9fafb' }}>
                                <div className="checkout-section-title">Price Summary</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div className="price-summary-row">
                                        <span>Bag Total ({cartItems.length} Items)</span>
                                        <span>₹{totalAmount.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="price-summary-row">
                                        <span>Delivery Fee</span>
                                        <span style={{ color: '#10b981', fontWeight: 700 }}>FREE</span>
                                    </div>
                                    <div className="price-summary-total">
                                        <span>Total Amount</span>
                                        <span>₹{finalAmount.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Success View */
                        <div className="success-wrapper">
                            <div className="success-icon-container">
                                <FaCheckCircle size={38} />
                            </div>

                            <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1f2937', margin: '0 0 8px 0' }}>Order Confirmed!</h2>
                            <p style={{ fontSize: '13.5px', color: '#6b7280', margin: '0 0 24px 0', lineHeight: '1.6' }}>
                                Thank you for shopping with us. Your order has been placed successfully and is now being processed.
                            </p>

                            <div className="success-details-card">
                                <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order Details</span>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#43c8ca' }}>
                                        {placedOrder?.order_id || 'ORD-PROCESSING'}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <span style={{ color: '#6b7280' }}>Payment Mode:</span>
                                    <span style={{ fontWeight: '700', color: '#1f2937' }}>{placedOrder?.payment_method || paymentMethod}</span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <span style={{ color: '#6b7280' }}>Total Amount:</span>
                                    <span style={{ fontWeight: '800', color: '#1f2937' }}>₹{(placedOrder?.total_amount || finalAmount).toLocaleString('en-IN')}</span>
                                </div>

                                {(placedOrder?.address || activeAddress) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', borderTop: '1px solid #e5e7eb', paddingTop: '10px', marginTop: '2px' }}>
                                        <span style={{ color: '#6b7280' }}>Deliver to:</span>
                                        <span style={{ fontWeight: '700', color: '#1f2937' }}>{(placedOrder?.address?.name || activeAddress?.name)}</span>
                                        <span style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: '1.4' }}>
                                            {placedOrder?.address?.address || activeAddress?.address}, {placedOrder?.address?.locality || activeAddress?.locality}, {placedOrder?.address?.city || activeAddress?.city}, {placedOrder?.address?.state || activeAddress?.state} - {placedOrder?.address?.pincode || activeAddress?.pincode}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <button
                                    onClick={() => {
                                        onClose();
                                        if (onOpenOrders) {
                                            onOpenOrders();
                                        }
                                    }}
                                    className="success-action-btn"
                                >
                                    View My Orders
                                </button>
                                <button
                                    onClick={onClose}
                                    className="success-secondary-btn"
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {cartItems.length > 0 && step !== 'success' && (
                    <div style={{ padding: '20px 24px', borderTop: '1px solid #f3f4f6', backgroundColor: '#ffffff', boxShadow: '0 -10px 30px rgba(0,0,0,0.02)' }}>
                        {step === 'cart' ? (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '16px', fontWeight: '800', color: '#1f2937' }}>
                                    <span>Subtotal</span>
                                    <span>₹{totalAmount.toLocaleString('en-IN')}</span>
                                </div>
                                <button
                                    onClick={() => {
                                        if (!isAuth) {
                                            setIsLoginModalOpen(true);
                                        } else {
                                            setStep('checkout');
                                        }
                                    }}
                                    className="success-action-btn"
                                >
                                    Proceed to Checkout
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={handlePlaceOrder}
                                className="success-action-btn"
                            >
                                Place Order (₹{finalAmount.toLocaleString('en-IN')})
                            </button>
                        )}
                    </div>
                )}
            </div>

            <AccountModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} name={name} />
            <AddressModal isOpen={isAddressModalOpen} onClose={() => setIsAddressModalOpen(false)} />
        </div>
    );
};

export default CartModal;
